'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Search, FileText, Lock, CheckCircle2 } from 'lucide-react';
import { useMountShuffledList } from './shuffle-utils';

interface EvidenceMatchMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    scenario?: string;
    theories?: any[];
    clues?: any[];
    _hasHints?: boolean;
  };
}

export default function EvidenceMatchMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: EvidenceMatchMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Search className="w-5 h-5" />}
      iconLabel="Evidence Match — Connect clues to the correct theories"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <EvidenceMatchMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}

function EvidenceMatchMechanicInner({ payload, submissionState, setAnswer }: any) {
  const scenario = payload?.scenario || "Review the evidence and build your case.";
  const theories = useMountShuffledList<any>(payload?.theories || []);
  const clues = useMountShuffledList<any>(payload?.clues || []);

  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [activeClueId, setActiveClueId] = useState<string | null>(null);

  useEffect(() => {
    // Only allow submission when all clues are assigned
    if (Object.keys(assignments).length === clues.length && clues.length > 0) {
      setAnswer(assignments);
    } else {
      setAnswer(null);
    }
  }, [assignments, clues.length, setAnswer]);

  const handleClueSelect = (clueId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    
    // If clicking an already assigned clue, unassign it
    if (assignments[clueId]) {
      setAssignments(prev => {
        const next = { ...prev };
        delete next[clueId];
        return next;
      });
      setActiveClueId(null);
    } else {
      setActiveClueId(activeClueId === clueId ? null : clueId);
    }
  };

  const handleTheoryClick = (theoryId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    if (activeClueId) {
      setAssignments(prev => ({ ...prev, [activeClueId]: theoryId }));
      setActiveClueId(null);
    }
  };

  const isTheoryUnlocked = (theoryId: string) => {
    const theory = theories.find((t: any) => t.id === theoryId);
    if (!theory || !theory.dependsOnTheoryId) return true;
    return Object.values(assignments).includes(theory.dependsOnTheoryId);
  };

  const unassignedClues = clues.filter((c: any) => !assignments[c.id]);

  return (
    <div className="flex flex-col gap-6 font-sans">
      {/* CASE BRIEF */}
      <div className="p-6 bg-muted/20 border border-border/50 rounded-2xl shadow-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
        <h4 className="text-xs uppercase font-bold text-muted-foreground mb-3 tracking-widest flex items-center gap-2">
          <FileText className="w-4 h-4" /> Case File
        </h4>
        <p className="font-serif text-lg leading-relaxed text-foreground/90">{scenario}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* THEORIES BOARD */}
        <div className="flex flex-col gap-4">
          <h4 className="text-xs uppercase font-bold text-muted-foreground tracking-widest">Hypothesis Board</h4>
          <div className="flex flex-col gap-4">
            {theories.map((theory: any) => {
              const unlocked = isTheoryUnlocked(theory.id);
              const assignedClues = clues.filter((c: any) => assignments[c.id] === theory.id);
              const isTarget = activeClueId && unlocked;

              return (
                <div 
                  key={theory.id}
                  onClick={() => unlocked && handleTheoryClick(theory.id)}
                  className={`
                    relative p-4 rounded-xl border-2 transition-all duration-300
                    ${!unlocked ? 'border-dashed border-border/40 bg-muted/10 opacity-60 grayscale' : ''}
                    ${unlocked && !isTarget ? 'border-border bg-card shadow-sm' : ''}
                    ${isTarget ? 'border-primary border-dashed bg-primary/5 cursor-pointer ring-4 ring-primary/10' : ''}
                  `}
                >
                  {!unlocked && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px] rounded-xl">
                      <Lock className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                  )}

                  <h5 className="font-medium text-sm mb-3 text-foreground/90">{theory.content}</h5>
                  
                  {/* SLOTTED CLUES */}
                  <div className="flex flex-col gap-2 min-h-[40px]">
                    {assignedClues.length === 0 && unlocked && (
                      <div className="text-xs text-muted-foreground/50 italic flex items-center h-full">
                        {isTarget ? 'Click here to assign selected clue...' : 'No clues assigned yet.'}
                      </div>
                    )}
                    {assignedClues.map((clue: any) => (
                      <div 
                        key={clue.id}
                        onClick={(e) => handleClueSelect(clue.id, e)}
                        className="p-3 bg-background rounded-lg border border-border shadow-sm text-sm cursor-pointer hover:border-destructive/50 hover:bg-destructive/5 transition-colors group flex items-start gap-2"
                      >
                        <Search className="w-3.5 h-3.5 mt-0.5 text-muted-foreground group-hover:text-destructive" />
                        <span className="leading-snug">{clue.content}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* EVIDENCE BOX */}
        <div className="flex flex-col gap-4">
          <h4 className="text-xs uppercase font-bold text-muted-foreground tracking-widest flex justify-between items-center">
            <span>Evidence Box</span>
            <span className="bg-muted px-2 py-0.5 rounded-full text-[10px]">
              {unassignedClues.length} remaining
            </span>
          </h4>
          
          <div className="bg-muted/30 border border-border/50 rounded-2xl p-5 min-h-[300px]">
            {unassignedClues.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
                <CheckCircle2 className="w-8 h-8 text-primary/50" />
                <p className="text-sm">All evidence has been assigned.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {unassignedClues.map((clue: any) => (
                  <button
                    type="button"
                    key={clue.id}
                    disabled={submissionState !== 'idle' && submissionState !== 'error'}
                    onClick={() => handleClueSelect(clue.id)}
                    className={`
                      text-left p-4 rounded-xl border-2 transition-all shadow-sm flex items-start gap-3
                      ${activeClueId === clue.id 
                        ? 'border-primary bg-primary text-primary-foreground shadow-[0_0_15px_rgba(var(--primary),0.3)] scale-[1.02]' 
                        : 'border-border bg-card hover:border-primary/50 hover:shadow-md'
                      }
                    `}
                  >
                    <Search className={`w-4 h-4 mt-0.5 ${activeClueId === clue.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`} />
                    <span className="text-sm leading-relaxed">{clue.content}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
